// ============================================================================
// CHANGO V5.0 — Timeline / activity feed (D6 frontend)
// ============================================================================
// Renders /api/activity/feed or /api/activity/entity/:type/:id — workspace or
// entity-scoped. Paginated via `before_id`, sorted DESC.
//
// Backend already strips sensitive payload keys for sales (cost/profit/margin)
// and drops SALES_BLOCKED_EVENTS entirely, so this component does NOT need
// any role awareness. We layer a RoleGate-lite inside EventRow anyway, as
// belt-and-braces for §20 坑 #17.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { api, HttpError } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

const PAGE_SIZE = 30;

const EVENT_LABEL = {
  'quote.created':            ['📝', '创建了报价'],
  'quote.updated':            ['✏️', '修改了报价'],
  'quote.approved':           ['✅', '审核通过'],
  'quote.rejected':           ['❌', '已拒绝'],
  'quote.issued':             ['📤', '出具报价单'],
  'quote.closed_won':         ['🏆', '成交'],
  'quote.closed_lost':        ['💤', '流失'],
  'quote.comment_added':      ['💬', '发表了评论'],
  'customer.created':         ['🧑', '新增客户'],
  'customer.updated':         ['✏️', '更新客户'],
  'customer.tags_updated':    ['🏷️', '更新客户标签'],
  'customer.contact_added':   ['📇', '新增联系人'],
  'customer.contact_updated': ['📇', '更新联系人'],
  'customer.contact_removed': ['📇', '删除联系人'],
  'order.created':            ['💰', '创建订单'],
  'order.cancelled':          ['🚫', '取消订单'],
  'refund.requested':         ['↩️', '申请退款'],
  'member.invited':           ['📨', '邀请成员'],
  'member.removed':           ['➖', '移除成员'],
  'workspace.activated':      ['🎉', '工作空间激活'],
  'workspace.suspended':      ['⏸️', '工作空间暂停']
};

function eventMeta(type) {
  const m = EVENT_LABEL[type];
  if (m) return m;
  return ['•', type];
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-CN', { hour12: false });
}

function payloadPreview(payload, role) {
  if (!payload || typeof payload !== 'object') return null;
  // Defensive scrub for sales role — backend already did this, but cheap.
  const BLOCKED = ['cost', 'profit', 'margin', 'cost_total', 'profit_total', 'margin_pct'];
  const keys = Object.keys(payload).filter((k) => {
    if (role === 'sales' && BLOCKED.includes(k)) return false;
    return true;
  }).slice(0, 3);
  if (!keys.length) return null;
  return keys.map((k) => `${k}=${formatVal(payload[k])}`).join(' · ');
}

function formatVal(v) {
  if (v == null) return '—';
  if (typeof v === 'string') return v.length > 40 ? v.slice(0, 38) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v).slice(0, 40);
}

function EventRow({ ev, role }) {
  const [icon, label] = eventMeta(ev.event_type);
  const preview = payloadPreview(ev.payload, role);
  return (
    <li className="flex items-start gap-3 py-3">
      <span aria-hidden className="text-lg leading-6">{icon}</span>
      <div className="flex-1 text-sm text-slate-700">
        <div>
          <span className="font-medium">{ev.actor_name || `u${ev.actor_id ?? '?'}`}</span>
          <span className="mx-1 text-slate-400">·</span>
          <span>{label}</span>
          {ev.entity_type && ev.entity_id && (
            <span className="ml-1 text-xs text-slate-400">#{ev.entity_type}/{ev.entity_id}</span>
          )}
        </div>
        {preview && <div className="mt-0.5 text-xs text-slate-500">{preview}</div>}
        <div className="mt-0.5 text-xs text-slate-400">{formatWhen(ev.created_at)}</div>
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {'workspace' | 'entity'} props.scope
 * @param {string} [props.entityType] required when scope==='entity'
 * @param {number|string} [props.entityId] required when scope==='entity'
 * @param {number} [props.pageSize]
 * @param {string} [props.className]
 */
export default function TimelineFeed({
  scope = 'workspace',
  entityType,
  entityId,
  pageSize = PAGE_SIZE,
  className = ''
}) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [beforeId, setBeforeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const abortRef = useRef(null);

  const buildUrl = useCallback((cursor) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(pageSize));
    if (cursor) qs.set('before_id', String(cursor));
    if (scope === 'entity') {
      if (!entityType || !entityId) return null;
      return `/api/activity/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?${qs.toString()}`;
    }
    return `/api/activity/feed?${qs.toString()}`;
  }, [scope, entityType, entityId, pageSize]);

  const load = useCallback(async (cursor) => {
    const url = buildUrl(cursor);
    if (!url) { setLoading(false); return; }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setErr(null);
    try {
      const data = await api.get(url, { signal: ctrl.signal });
      const rows = Array.isArray(data?.events) ? data.events : [];
      if (cursor) {
        setEvents((prev) => [...prev, ...rows]);
      } else {
        setEvents(rows);
      }
      if (rows.length < pageSize) {
        setHasMore(false);
      } else {
        setBeforeId(rows[rows.length - 1].id);
        setHasMore(true);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErr(e instanceof HttpError ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [buildUrl, pageSize]);

  useEffect(() => {
    setEvents([]);
    setBeforeId(null);
    setHasMore(true);
    load(null);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [load]);

  return (
    <section className={['card p-4', className].join(' ')}>
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">
          {scope === 'entity' ? '动态' : '工作空间动态'}
        </h3>
        <button type="button" className="btn-ghost text-sm"
          onClick={() => { setEvents([]); setBeforeId(null); load(null); }}>
          刷新
        </button>
      </header>

      {err && <p className="err text-sm mb-2">{err}</p>}

      {events.length === 0 && !loading ? (
        <p className="text-sm text-slate-500">暂无动态。</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {events.map((ev) => (
            <EventRow key={ev.id} ev={ev} role={user?.role} />
          ))}
        </ul>
      )}

      <div className="mt-3 flex justify-center">
        {hasMore ? (
          <button type="button" className="btn-ghost text-sm" disabled={loading}
            onClick={() => load(beforeId)}>
            {loading ? '加载中…' : '加载更多'}
          </button>
        ) : events.length > 0 ? (
          <span className="text-xs text-slate-400">已到底部</span>
        ) : null}
      </div>
    </section>
  );
}
